/**
 * Critical Controls Card Component
 *
 * Displays the most frequently-used configuration options in a
 * always-visible card with improved visual hierarchy and scannability.
 */

import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Slider,
  Paper,
  Grid,
  SelectChangeEvent,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { StorageTopology, DriveParameters, CapacityUtilization } from '../../types';
import { validateField } from '../../data/validation';
import { TooltipIcon, ValidationIcon } from '../ui';

interface CriticalControlsProps {
  topology: StorageTopology;
  drive: DriveParameters;
  utilization: CapacityUtilization;
  onTopologyChange: (value: StorageTopology) => void;
  onDriveChange: (value: DriveParameters) => void;
  onUtilizationChange: (value: CapacityUtilization) => void;
}

/**
 * CriticalControls - Always-visible controls for common adjustments
 */
export function CriticalControls({
  topology,
  drive,
  utilization,
  onTopologyChange,
  onDriveChange,
  onUtilizationChange,
}: CriticalControlsProps) {
  // Validation for drivesPerVdev
  const drivesPerVdevValidations = validateField('drivesPerVdev', topology.drivesPerVdev);

  // Handle RAID level change
  const handleRAIDChange = (event: SelectChangeEvent) => {
    onTopologyChange({ ...topology, raidLevel: event.target.value as StorageTopology['raidLevel'] });
  };

  // Handle drive count changes with +/- buttons
  const handleDriveCountChange = (field: 'drivesPerVdev' | 'vdevCount', delta: number) => {
    const newValue = (topology[field] + delta);
    if (newValue >= 1 && newValue <= 24) {
      onTopologyChange({ ...topology, [field]: newValue });
    }
  };

  // Handle pool fill slider change
  const handlePoolFillChange = (_event: Event, newValue: number | number[]) => {
    onUtilizationChange({ ...utilization, poolFillPercent: newValue as number });
  };

  // Risk level based on fill percentage
  const getRiskLevel = (fillPercent: number) => {
    if (fillPercent < 50) return { level: 'low', color: '#4caf50' as const };
    if (fillPercent < 75) return { level: 'medium', color: '#ff9800' as const };
    if (fillPercent < 90) return { level: 'high', color: '#ff5722' as const };
    return { level: 'critical', color: '#f44336' as const };
  };

  const risk = getRiskLevel(utilization.poolFillPercent);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Section 1: Pool Topology */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.8rem' }}>
          Pool Topology
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* RAID Level */}
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.75rem' }}>
              RAID Level
              <TooltipIcon tooltipKey="raidLevel" />
            </InputLabel>
            <Select
              value={topology.raidLevel}
              label="RAID Level"
              onChange={handleRAIDChange}
            >
              <MenuItem value="mirror">
                <Box>
                  <strong>Mirror</strong>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Best performance, 50% efficiency
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="raidz1">
                <Box>
                  <strong>RAIDZ1</strong>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Single parity, good for vdevs ≤ 5 drives
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="raidz2">
                <Box>
                  <strong>RAIDZ2</strong>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Dual parity, recommended for most use cases
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="raidz3">
                <Box>
                  <strong>RAIDZ3</strong>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    Triple parity, maximum redundancy
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {/* Drive Counts - Horizontal Layout */}
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                Drives/vdev
                <TooltipIcon tooltipKey="drivesPerVdev" />
                <ValidationIcon validations={drivesPerVdevValidations} />
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => handleDriveCountChange('drivesPerVdev', -1)}
                  disabled={topology.drivesPerVdev <= 1}
                  sx={{ p: 0.5 }}
                >
                  <RemoveIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                </IconButton>
                <TextField
                  type="number"
                  value={topology.drivesPerVdev}
                  onChange={(e) => handleDriveCountChange('drivesPerVdev', Number(e.target.value) - topology.drivesPerVdev)}
                  inputProps={{ min: 1, max: 24, style: { textAlign: 'center', fontSize: '0.8rem', padding: '4px' } }}
                  sx={{ width: '100%', '& .MuiInputBase-input': { p: '4px' } }}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleDriveCountChange('drivesPerVdev', 1)}
                  disabled={topology.drivesPerVdev >= 24}
                  sx={{ p: 0.5 }}
                >
                  <AddIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
                vdev Count
                <TooltipIcon tooltipKey="vdevCount" />
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => handleDriveCountChange('vdevCount', -1)}
                  disabled={topology.vdevCount <= 1}
                  sx={{ p: 0.5 }}
                >
                  <RemoveIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                </IconButton>
                <TextField
                  type="number"
                  value={topology.vdevCount}
                  onChange={(e) => handleDriveCountChange('vdevCount', Number(e.target.value) - topology.vdevCount)}
                  inputProps={{ min: 1, max: 24, style: { textAlign: 'center', fontSize: '0.8rem', padding: '4px' } }}
                  sx={{ width: '100%', '& .MuiInputBase-input': { p: '4px' } }}
                  size="small"
                />
                <IconButton
                  size="small"
                  onClick={() => handleDriveCountChange('vdevCount', 1)}
                  disabled={topology.vdevCount >= 24}
                  sx={{ p: 0.5 }}
                >
                  <AddIcon fontSize="small" sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Box>
            </Grid>
          </Grid>

          {drivesPerVdevValidations.length > 0 && (
            <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem', display: 'block' }}>
              {drivesPerVdevValidations[0].message}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Total drives: <strong>{topology.drivesPerVdev * topology.vdevCount}</strong>
          </Typography>
        </Box>
      </Paper>

      {/* Section 2: Drive Configuration */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.8rem' }}>
          Drive Configuration
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Drive Size */}
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
              Drive Size (TB)
              <TooltipIcon tooltipKey="driveSizeTB" />
            </Typography>
            <TextField
              type="number"
              value={drive.driveSizeTB}
              onChange={(e) => onDriveChange({ ...drive, driveSizeTB: Number(e.target.value) })}
              inputProps={{ min: 1, max: 30, step: 0.5 }}
              fullWidth
              size="small"
              sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            Configure cost, power, AFR in <strong>Advanced Settings</strong> below
          </Typography>
        </Box>
      </Paper>

      {/* Section 3: Pool Utilization */}
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, fontSize: '0.8rem' }}>
          Pool Utilization
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
              Fill Level: <strong>{utilization.poolFillPercent}%</strong>
              <TooltipIcon tooltipKey="poolFillPercent" />
            </Typography>
            <Slider
              value={utilization.poolFillPercent}
              onChange={handlePoolFillChange}
              min={0}
              max={100}
              size="small"
              marks={[
                { value: 0, label: '0%' },
                { value: 50, label: '50%' },
                { value: 80, label: '80%' },
                { value: 95, label: '95%' }
              ]}
              valueLabelDisplay="auto"
              sx={{
                color: utilization.poolFillPercent > 80 ? 'warning' : 'primary',
                height: 4,
                '& .MuiSlider-thumb': { width: 12, height: 12 },
              }}
            />
          </Box>

          <Box
            sx={{
              p: 0.75,
              bgcolor: `${risk.color}10`,
              borderRadius: 1,
              border: `1px solid ${risk.color}`,
            }}
          >
            <Typography variant="caption" sx={{ color: risk.color, fontWeight: 500, fontSize: '0.7rem' }}>
              Risk: {risk.level.toUpperCase()}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
